import {
  useMutation,
  UseMutationOptions,
  UseMutationResult,
} from '@tanstack/react-query';
import { QueryMap } from '~/libs/api-hooks/architect/utils/prepareSearchParams';
import requester, {
  BodyType,
  CCRequest,
  HttpMethods,
  UrlOptions,
} from '~/libs/api-hooks/architect/utils/requester';
import StatusError from '~/libs/api-hooks/architect/utils/statusError';
import DeepPartial from '~/libs/api-hooks/architect/utils/DeepPartial';
import mapApiObjectToCamelCase from '../utils/mapApiObjectToCamelCase';

export type MutationApiOptions<
  ResponseDataType,
  MutatorRequesterProps = void,
  DeepPartialResponseDataType extends
    DeepPartial<ResponseDataType> = DeepPartial<ResponseDataType>,
> = Omit<
  UseMutationOptions<
    DeepPartialResponseDataType,
    StatusError,
    MutatorRequesterProps
  >,
  'mutationKey' | 'mutationFn'
>;

export type MutationApiResult<
  ResponseDataType,
  MutatorRequesterProps = void,
  DeepPartialResponseDataType extends
    DeepPartial<ResponseDataType> = DeepPartial<ResponseDataType>,
> = UseMutationResult<
  DeepPartialResponseDataType,
  StatusError,
  MutatorRequesterProps
>;

export type MutationMutatorRequesterResult = Partial<CCRequest>;

export interface UseMutationApiProps<
  ResponseDataType,
  MutatorRequesterProps = void,
  DeepPartialResponseDataType extends
    DeepPartial<ResponseDataType> = DeepPartial<ResponseDataType>,
> {
  pathname: string;
  mutationKey: string[];
  method: HttpMethods;
  queryMap?: QueryMap;
  body?: BodyType | BodyType[];
  reactQueryOptions?: MutationApiOptions<
    ResponseDataType,
    MutatorRequesterProps,
    DeepPartialResponseDataType
  >;
  urlOptions?: UrlOptions;
  includeApiKeyWhenAvailable?: boolean;
  sendCsrfToken?: boolean;
  mutatorRequester?: (props: MutatorRequesterProps) => Partial<CCRequest>;
  customHeaders?: Record<string, string>;
  timeoutMs?: number;
}

const useMutationApi = <
  ResponseDataType,
  MutatorRequesterProps = void,
  DeepPartialResponseDataType extends
    DeepPartial<ResponseDataType> = DeepPartial<ResponseDataType>,
>({
  pathname,
  mutationKey,
  method,
  queryMap,
  body,
  reactQueryOptions = {},
  urlOptions,
  includeApiKeyWhenAvailable = true,
  sendCsrfToken = true,
  mutatorRequester,
  customHeaders,
  timeoutMs,
}: UseMutationApiProps<
  ResponseDataType,
  MutatorRequesterProps,
  DeepPartialResponseDataType
>): MutationApiResult<
  ResponseDataType,
  MutatorRequesterProps,
  DeepPartialResponseDataType
> => {
  const fetchAPI = async (mutatorRequesterProps: MutatorRequesterProps) => {
    let requesterProps: Partial<CCRequest> = {};
    try {
      if (mutatorRequester) {
        requesterProps = mutatorRequester(mutatorRequesterProps);
      }
    } catch (error: any) {
      throw new StatusError({
        message: error.message,
        status: 0,
        pathname,
      });
    }

    const response = await requester({
      pathname,
      method,
      queryMap,
      body,
      urlOptions,
      includeApiKeyWhenAvailable,
      sendCsrfToken,
      customHeaders,
      timeoutMs,
      ...requesterProps,
    });

    let text: string | undefined = undefined;
    try {
      text = await response.text();
      return mapApiObjectToCamelCase<DeepPartialResponseDataType>(
        JSON.parse(text),
      );
    } catch {
      // if we get to this point DeepPartialResponseDataType will either be undefiend or string type.
      // Tests have been added to for the edge cases where the above statement is false
      return (text
        ? text
        : undefined) as unknown as DeepPartialResponseDataType;
    }
  };

  return useMutation<
    DeepPartialResponseDataType,
    StatusError,
    MutatorRequesterProps
  >(mutationKey, fetchAPI, reactQueryOptions);
};

export default useMutationApi;
